import type { AdminConfigRow, ClientConfigRow, ConfigDefinition } from '@inboundx/shared/config';
import { AdminConfigResolver, ConfigResolver } from '@inboundx/shared/config';
import type { Json } from '@inboundx/shared/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getSchemaBySlug } from '@/config/schemas';
import { mergeWorkspaceConfigRows, workspaceOverride } from '@/config/workspaceConfig';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import type { SelfServePlan, SelfServePlanAssignment } from '@/domain/selfServePlans';
import { queryKeys } from '@/lib/queryKeys';
import {
    fetchAdminConfigs,
    fetchClient,
    fetchClientConfigs,
    fetchConfigDefinitions,
    fetchDomain,
    fetchSelfServePlan,
    mergeOnboardingState,
    selectSelfServePlan,
    setSelfServeQuotaStartedAt,
    upsertAdminConfig,
    upsertClientConfig,
} from '@/services/config';
import { formatSupabaseError } from '@/utils/supabaseError';

export interface ResolvedConfigData {
    resolver: ConfigResolver;
    definitions: ConfigDefinition[];
    clientConfigs: ClientConfigRow[];
}

interface ResolvedAdminConfigData {
    resolver: AdminConfigResolver;
    definitions: ConfigDefinition[];
    adminConfigs: AdminConfigRow[];
}

/**
 * `configKey` differs from `domain` only for a workspace placed under a path: its rows
 * are laid over its host's, as `config.workspace_config_rows` does for the runtime.
 */
export async function fetchResolvedConfigData(
    domain: string,
    configKey: string = domain
): Promise<ResolvedConfigData> {
    // Supabase rejects with plain objects; as `Error`s, the pages' `error.message` shows the cause.
    const asError = (err: unknown): never => {
        if (err instanceof Error) throw err;
        const { title, detail } = formatSupabaseError(err, 'Failed to load configuration');
        throw new Error(detail ? `${title}: ${detail}` : title);
    };

    const domainRow = await fetchDomain(domain).catch(asError);
    if (!domainRow) throw new Error(`Domain not found: ${domain}`);

    const [client, definitions, hostConfigs, ownConfigs] = await Promise.all([
        fetchClient(domainRow.client_id),
        fetchConfigDefinitions(),
        fetchClientConfigs(domain),
        configKey === domain ? null : fetchClientConfigs(configKey),
    ]).catch(asError);

    if (!client) throw new Error(`Client not found for domain: ${domain}`);

    const clientConfigs = ownConfigs
        ? mergeWorkspaceConfigRows(hostConfigs, ownConfigs, definitions, configKey)
        : hostConfigs;

    return {
        resolver: new ConfigResolver(domainRow, client, definitions, clientConfigs),
        definitions,
        clientConfigs,
    };
}

export function useResolvedConfig() {
    const { selectedDomain, selectedConfigKey, loading: workspaceLoading } = useWorkspace();

    const query = useQuery<ResolvedConfigData>({
        // Two workspaces on one host share selectedDomain; the config key tells them apart.
        queryKey: queryKeys.config.resolved(selectedConfigKey),
        queryFn: () => fetchResolvedConfigData(selectedDomain, selectedConfigKey),
        enabled: !!selectedDomain && !!selectedConfigKey && !workspaceLoading,
        staleTime: 5 * 60 * 1000,
    });
    // A disabled query is pending but not loading (TanStack v5). Without this, pages
    // render their "Failed to load configuration" state until the workspace resolves.
    // A Proxy, not a spread: TanStack re-renders a caller only for the fields it reads.
    return new Proxy(query, {
        get: (target, key) =>
            key === 'isLoading' ? target.isLoading || workspaceLoading : Reflect.get(target, key),
    });
}

export function useSelfServePlanQuery(domain: string, enabled = true) {
    return useQuery<SelfServePlanAssignment | null>({
        queryKey: queryKeys.config.selfServePlan(domain),
        queryFn: () => fetchSelfServePlan(domain),
        enabled: enabled && !!domain,
        staleTime: 5 * 60 * 1000,
    });
}

interface SaveConfigSectionParams {
    slug: string;
    scope: string;
    enabled?: boolean | null;
    config: Record<string, unknown> | null;
}

/**
 * Saves a section of the selected workspace. The hook owns the target: a caller that
 * named a hostname would write a path-rooted workspace's edits to its sibling.
 */
export function useSaveConfigSection() {
    const queryClient = useQueryClient();
    const { selectedDomain, selectedConfigKey } = useWorkspace();

    return useMutation({
        mutationFn: async (params: SaveConfigSectionParams) => {
            if (!selectedConfigKey) throw new Error('No workspace selected');
            const submitted = { enabled: params.enabled, config: params.config };
            if (selectedConfigKey === selectedDomain) {
                return upsertClientConfig(selectedConfigKey, params.slug, params.scope, submitted);
            }

            // Under a path: store only what differs from the host, read fresh so the
            // difference is not taken against a stale copy.
            const [definitions, hostRows] = await Promise.all([
                fetchConfigDefinitions(),
                fetchClientConfigs(selectedDomain),
            ]);
            const definition = definitions.find(
                (d) => d.slug === params.slug && d.scope === params.scope
            );
            if (!definition) throw new Error(`Unknown configuration section: ${params.slug}`);
            const schemaDefaults =
                (getSchemaBySlug(params.slug)?.default as Record<string, unknown> | undefined) ??
                {};
            const hostRow = hostRows.find(
                (row) => row.config_slug === params.slug && row.config_scope === params.scope
            );
            return upsertClientConfig(
                selectedConfigKey,
                params.slug,
                params.scope,
                workspaceOverride(definition, schemaDefaults, hostRow, submitted)
            );
        },
        onSuccess: (_data, variables) => {
            // Traffic control changes affect widget state badges on the Clients page
            if (variables.slug === 'traffic_control') {
                queryClient.invalidateQueries({
                    queryKey: queryKeys.clients.all,
                });
            }
            // Returned, not fired and forgotten: react-query awaits a promise from onSuccess,
            // so `isPending` (and `mutateAsync`) only settle once the resolved config has
            // actually refetched. Callers that compare against it — the Playground rail's
            // dirty state, for one — would otherwise keep showing the pre-save baseline for
            // the length of the refetch, and keep it forever if the refetch failed.
            return queryClient.invalidateQueries({
                queryKey: queryKeys.config.resolved(selectedConfigKey),
            });
        },
        onError: (error) => {
            toast.error(error instanceof Error ? error.message : 'Failed to save configuration');
        },
    });
}

interface MergeOnboardingStateParams {
    domain: string;
    patch: Json;
}

export function useMergeOnboardingState() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ domain, patch }: MergeOnboardingStateParams) =>
            mergeOnboardingState(domain, patch),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({
                queryKey: queryKeys.config.resolved(variables.domain),
            });
        },
        onError: (error) => {
            toast.error(error instanceof Error ? error.message : 'Failed to update onboarding');
        },
    });
}

interface SelectSelfServePlanParams {
    domain: string;
    plan: SelfServePlan;
}

export function useSelectSelfServePlan() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ domain, plan }: SelectSelfServePlanParams) =>
            selectSelfServePlan(domain, plan),
        onSuccess: (_data, variables) =>
            Promise.all([
                queryClient.invalidateQueries({
                    queryKey: queryKeys.config.selfServePlan(variables.domain),
                }),
                queryClient.invalidateQueries({
                    queryKey: queryKeys.config.resolved(variables.domain),
                }),
                queryClient.invalidateQueries({
                    queryKey: queryKeys.planUsage.detail(variables.domain),
                }),
            ]),
        onError: (error) => {
            toast.error(error instanceof Error ? error.message : 'Failed to select plan');
        },
    });
}

interface SetSelfServeQuotaStartedAtParams {
    domain: string;
    quotaStartedAt: string;
}

export function useSetSelfServeQuotaStartedAt() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ domain, quotaStartedAt }: SetSelfServeQuotaStartedAtParams) =>
            setSelfServeQuotaStartedAt(domain, quotaStartedAt),
        onSuccess: (_data, variables) =>
            Promise.all([
                queryClient.invalidateQueries({
                    queryKey: queryKeys.config.selfServePlan(variables.domain),
                }),
                queryClient.invalidateQueries({
                    queryKey: queryKeys.planUsage.detail(variables.domain),
                }),
            ]),
        onError: (error) => {
            toast.error(error instanceof Error ? error.message : 'Failed to update quota start');
        },
    });
}

export function useResolvedAdminConfig() {
    return useQuery<ResolvedAdminConfigData>({
        queryKey: queryKeys.config.admin,
        queryFn: async () => {
            const [definitions, adminConfigs] = await Promise.all([
                fetchConfigDefinitions(),
                fetchAdminConfigs(),
            ]);
            return {
                resolver: new AdminConfigResolver(definitions, adminConfigs),
                definitions,
                adminConfigs,
            };
        },
        staleTime: 5 * 60 * 1000,
    });
}

interface SaveAdminConfigSectionParams {
    slug: string;
    enabled?: boolean | null;
    config: Record<string, unknown> | null;
}

export function useSaveAdminConfigSection() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (params: SaveAdminConfigSectionParams) =>
            upsertAdminConfig(params.slug, {
                enabled: params.enabled,
                config: params.config,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.config.admin });
            queryClient.invalidateQueries({ queryKey: queryKeys.stats.all });
        },
        onError: (error) => {
            toast.error(error instanceof Error ? error.message : 'Failed to save configuration');
        },
    });
}
